import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { SWRConfiguration } from 'swr';

import {
  disableReadOnlyRehireFields,
  removeNavButtonSettings,
  removeSsnFieldIfManagerRestricted,
} from '~/components/Forms/helper';
import { useApplicationConfig } from '~/data/applicationConfig/useApplicationConfig';
import { useCompanyData } from '~/data/company';
import { useGetCompanyOffices } from '~/data/companyOffices';
import { useAllCostCenters } from '~/data/costCenters';
import { useCustomEmployeeData } from '~/data/customData';
import {
  EmployeeUnitPosition,
  useEmployee,
  useEmploymentData,
} from '~/data/employee';
import { useAllEmployees } from '~/data/employees';
import {
  EmploymentDetailType,
  useEmploymentDetails,
} from '~/data/employment/useEmploymentDetails';
import { useAllEnums } from '~/data/enums';
import { useAllLegalEntities } from '~/data/legalEntity';
import { useOrgTree } from '~/data/orgunits';
import { Roles } from '~/data/user';
import { useContracts } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useContracts';
import { Contracts } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { useSelectedEmployeeId } from '~/hooks/employee';
import {
  useFormTemplateDetails,
  useFormTemplatesList,
  useFormTranslations,
  useFormsCustomConfig,
  useLatestTemplateVersion,
  useWorkflowConfig,
} from '~/hooks/forms';
import { useEmployeeViews } from '~/scenes/PortalViews/Views/EmployeeViews/useEmployeeViews';
import { EmployeeStatusEnum } from '~/services/Http/client.people.generated';
import { FormioComponent, FormioFormBuilderConfig } from '~/types/forms';

type PersonnelGroupFormkeys = {
  [key: `fEmplDetailPersonnelGroup${number}`]: string;
};

type CostCenterObj = {
  fEmpCostCenter: number | `${number}`;
  fEmpCostCenterIsDefault?: boolean;
};

export type FormsRehire = {
  empId: number;
  firstName: string;
  lastName: string;
  givenNames: string;
  ssn: string;
  birthdate: string;
  gender: string;
  nationality?: { group?: string; code?: string; name?: string };
  employeeNumber: string;
  employeeNumberUI: string;
  legalEntity: { id?: number; code?: string; name?: string };
  internalPosition: string;
  businessTitle?: string;
  orgUnit?: { id?: number; number?: string; name?: string };
  positionManager?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    givenNames?: string;
    employeeNumber?: string;
    status?: string;
  };
  unitPositions?: EmployeeUnitPosition[];
  internalPositionInfo?: string;
  internalPositionStartDate?: string;
  mainCostCenter?: { id?: number; code?: string; name?: string };
  addlCostCenter?: { id?: number; code?: string; name?: string }[];
  costCenters: CostCenterObj[];
  contractNumber?: string;
  currency?: { group?: string; code?: string; name?: string };
  employmentType?: { group?: string; code?: string; name?: string };
  employmentStatus?: { group?: string; code?: string; name?: string };
  employmentStartDate?: string;
  arrivalDate?: string;
  trialPeriod?: { id?: string; name?: string };
  trialPeriodEndDate?: string;
  fixedTermReason?: string;
  plannedEndDate?: string;
  employmentEndDate?: string;
  terminationDate?: string;
  terminationReason?: string;
  jobGrade?: { group?: string; code?: string; name?: string };
  taxNumber?: string;
  vacationBalanceCalculation?: string;
  office?: { id?: number; name?: string };
  workHoursStartDate?: string;
  calculateHoursBalance?: boolean;
  isMainContract: boolean;
  personnelGroup1?: { group: string; code?: string; name?: string } | null;
  personnelGroup2?: { group: string; code?: string; name?: string } | null;
  personnelGroup3?: { group: string; code?: string; name?: string } | null;
  personnelGroup4?: { group: string; code?: string; name?: string } | null;
  personnelGroup5?: { group: string; code?: string; name?: string } | null;
  employmentInfo?: string;
  isRehireForm: boolean;
};

type CustomDataObj = { group: string; code: string; name: string };

type ExistingCustomDataItem = Record<
  string,
  string | CustomDataObj | CustomDataObj[]
>;

export const useFormsRehire = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const { employeeId } = useParams<{ employeeId: string }>();

  const swrConfig: SWRConfiguration = {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  };

  const {
    data: offices,
    isLoading: officeDataLoading,
    getOfficeById,
  } = useGetCompanyOffices(swrConfig);
  const contracts = useContracts(Number(employeeId), {
    shouldRetryOnError: false,
  });
  const { isLoading: contractsLoading } = contracts;

  const { isLoading: isEmployeeViewsLoading } = useEmployeeViews();

  const {
    employee: { empBasicData, empPosition, empWorkContact },
    isLoading: employeeLoading,
  } = useEmployee();
  const {
    selectedEmployeeDetails: { employeeData },
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();

  const userHasManagerRoleOnly = hasRoleAccessOverEmployee(
    [Roles.MANAGER],
    [Roles.HR_ADMIN, Roles.HR_STAFF],
  );

  const { applicationConfig } = useApplicationConfig();
  const managerConfig = applicationConfig.fComConfig.mgrConfig;
  const managerCanViewSsn = managerConfig.fComEnableMgrViewSsn;
  const managerCanRehire = managerConfig.fComEnableMgrRehireEmployee;

  const {
    data: [employmentData],
    isLoading: employmentLoading,
  } = useEmploymentData({ employeeId: +employeeId }, swrConfig);
  const { costCenters, isLoading: isCostCentersLoading } = useAllCostCenters();
  const { legalEntities, isLoading: isLegalentitiesLoading } =
    useAllLegalEntities();
  const { orgTree, isLoading: isOrgTreeLoading } = useOrgTree();
  const { companyData, isLoading: isCompanyDataLoading } = useCompanyData();
  const { allEmployees, isLoading: isAllEmployeesLoading } = useAllEmployees();

  const { data: emplDetails, isLoading: isLoadingEmploymentDetails } =
    useEmploymentDetails(+employeeId, employeeData?.employmentId);
  const { allEnums, isLoading: isLoadingEnums } = useAllEnums();

  const { data: formTemplates, isLoading: formTemplatesLoading } =
    useFormTemplatesList();
  const { data: latestVersion, isLoading: latestVersionLoading } =
    useLatestTemplateVersion();
  const {
    data: formTemplateDetails,
    isLoading: formConfigLoading,
    mutate: refetchTemplateData,
  } = useFormTemplateDetails(selectedTemplateId, swrConfig);
  const { data: formTranslations, isLoading: formTranslationsLoading } =
    useFormTranslations(t);

  const { workflowConfig, isLoading: isLoadingWorkflowSettings } =
    useWorkflowConfig({ revalidateOnFocus: false });

  const {
    applyCustomConfig,
    customDataComponents,
    isLoading: isCustomConfigLoading,
  } = useFormsCustomConfig(selectedTemplateId);

  const {
    data: customDataSubmission,
    isLoading: isCustomDataSubmissionLoading,
  } = useCustomEmployeeData(Number(employeeId), swrConfig);

  const { employmentDetails } = useMemo(() => {
    return {
      employmentDetails: emplDetails
        ? emplDetails.find(
            (empDetails) => empDetails.fEmplUid === EmployeeStatusEnum.ACTIVE,
          )
        : undefined,
      pendingEmploymentDetails: emplDetails
        ? emplDetails.find(
            (empDetails) => empDetails.fEmplUid === EmployeeStatusEnum.PENDING,
          )
        : undefined,
    };
  }, [emplDetails]);

  const contractsData = contracts.data;
  const isMainContract = useMemo(
    () =>
      contractsData?.contracts?.find(
        (con: Contracts) => con.employeeId === +employeeId,
      )?.isDefault ?? false,
    [contractsData, employeeId],
  );

  // Memoize so the returned reference is stable across renders. existingData
  // is a useEffect dep in RehireEmployee.tsx and an unstable reference rebuilds
  // the Formio form on every parent re-render, wiping the user's input.
  const existingData = useMemo<FormsRehire>(() => {
    const jobGradeEnums = allEnums.JOB_GRADE || [];
    const jobGrade = jobGradeEnums.find(
      (item) => item.code === employmentDetails?.fEmplDetailJobGrade,
    );
    const employmentStatusEnums = allEnums.EMPLOYMENT_STATUS || [];
    const employmentStatus = employmentStatusEnums.find(
      (item) => item.code === employmentDetails?.fEmplDetailStatus,
    );
    const employmentTypeEnums = allEnums.EMPLOYMENT_TYPE || [];
    const employmentType = employmentTypeEnums.find(
      (item) => item.code === employmentData?.fEmplType,
    );
    const currencyEnums = allEnums.CURRENCY || [];
    const currency = currencyEnums.find(
      (item) => item.code === employmentData?.fEmplCurrency,
    );
    const legalEntity = legalEntities.find(
      (le) => le.fLegalEntityId === empPosition?.fEmpLegalEntity,
    );
    const legalEntityTransformed = {
      id: legalEntity?.fLegalEntityId,
      code: legalEntity?.fLegalEntityCode,
      name: legalEntity?.fLegalEntityName,
    };
    const orgUnits = orgTree[0]?.units?.map((unit) => ({
      id: +unit.fTreeUnitId,
      number: unit.fTreeUnitNumber,
      name: unit.fTreeUnitName,
      managerId: +unit.fTreeManagerId,
    }));
    const orgUnit = orgUnits?.find(
      (ou) => +ou.id === empPosition?.fInternalPositionUnit,
    );
    const [employeeCostCenter, ...employeeAddlCostCenters] =
      empPosition?.fEmpCostCenters || [];
    const costCenterTransformed = costCenters.find(
      (cc) => cc.fCostCenterId === employeeCostCenter?.fEmpCostCenter,
    );
    const costCenter = {
      id: costCenterTransformed?.fCostCenterId,
      code: costCenterTransformed?.fCostCenterCode,
      name: costCenterTransformed?.fCostCenterName,
    };
    const addlCostCenter = employeeAddlCostCenters.map((addlCC) => {
      const cc = costCenters.find(
        (c) => c.fCostCenterId === addlCC?.fEmpCostCenter,
      );
      return {
        id: cc?.fCostCenterId,
        code: cc?.fCostCenterCode,
        name: cc?.fCostCenterName,
      };
    });
    const manager = allEmployees[empPosition?.fInternalPositionManager];
    const positionManager = {
      id: manager?.fEmpId,
      firstName: manager?.fEmpFirstName,
      lastName: manager?.fEmpLastName,
      givenNames: manager?.fEmpGivenNames,
      employeeNumber: manager?.fEmpPersonNumber,
      status: manager?.fEmpStatus,
    };
    const { fComMaxPersonnelGroups } = companyData.config;
    const personnelGroupIndexNumbers: number[] = Array.from(
      Array(fComMaxPersonnelGroups).keys(),
    );
    const pgGroupsDefaultValue = personnelGroupIndexNumbers.reduce(
      (acc, item) => {
        const value: string =
          employmentDetails?.[
            `fEmplDetailPersonnelGroup${item + 1}` as keyof EmploymentDetailType
          ] ?? '';
        const enumValue = allEnums[`PERSONNEL_GROUP_${item + 1}`]?.find(
          (pg) => pg.code === value,
        );
        return {
          ...acc,
          [`personnelGroup${item + 1}`]: enumValue
            ? {
                group: `PERSONNEL_GROUP_${item + 1}`,
                code: enumValue?.code || '',
                name: enumValue?.name || '',
              }
            : null,
        };
      },
      {} as PersonnelGroupFormkeys,
    );
    const countries = allEnums.COUNTRY || [];
    const country = countries.find(
      (c) => c.code === empBasicData?.fEmpNationality,
    );
    const countryTransformed = {
      group: 'COUNTRY',
      code: country?.code,
      name: country?.name,
    };
    const office = getOfficeById(empWorkContact?.fEmpOfficeLoc);
    const officeTransformed = {
      id: office?.fOfficeId || -1,
      name: office?.fOfficeName || '',
    };
    const trialPeriod = {
      id: employmentData?.fEmplTrialPeriod,
      name: employmentData?.fEmplTrialPeriod,
    };

    return {
      empId: +employeeId,
      isRehireForm: true,
      ssn: empBasicData?.fEmpSocSecNum || '',
      firstName: empBasicData?.fEmpFirstName,
      lastName: empBasicData?.fEmpLastName,
      givenNames: empBasicData?.fEmpGivenNames,
      birthdate: empBasicData?.fEmpDateOfBirth,
      gender: empBasicData?.fEmpGender || '',
      ...(countryTransformed.code ? { nationality: countryTransformed } : {}),
      employeeNumber: empBasicData?.fEmpPersonNumber,
      // Set employeeNumberUI as well: the visible textfield is employeeNumberUI
      // and the hidden employeeNumber is calculated from it. Without this, the
      // hidden field's calculateValue clobbers employeeNumber to undefined and
      // the customDefaultValue ends up generating a fresh number on submit.
      employeeNumberUI: empBasicData?.fEmpPersonNumber,
      legalEntity: legalEntityTransformed,
      internalPosition: empPosition?.fInternalPositionPosition || '',
      businessTitle: empPosition?.fEmpJobTitle,
      orgUnit,
      ...(positionManager?.id ? { positionManager } : {}),
      unitPositions: empPosition?.fEmpUnitPositions,
      internalPositionInfo: empPosition?.fInternalPositionInfo || '',
      internalPositionStartDate: empPosition?.fInternalPositionStartDate,
      mainCostCenter: costCenter,
      addlCostCenter,
      costCenters: [],
      contractNumber: '',
      currency,
      employmentType,
      employmentStatus,
      employmentStartDate: '',
      arrivalDate: '',
      ...(trialPeriod?.id ? { trialPeriod } : {}),
      trialPeriodEndDate: '',
      fixedTermReason: '',
      plannedEndDate: '',
      employmentEndDate: '',
      terminationDate: '',
      terminationReason: '',
      jobGrade,
      taxNumber: employmentDetails?.fEmplDetailTaxNumber ?? '',
      vacationBalanceCalculation: `${
        employmentDetails?.fEmplDetailVacBalCalcSixDays ?? '5'
      }`,
      office: officeTransformed,
      workHoursStartDate: '',
      isMainContract,
      calculateHoursBalance: employmentDetails?.fEmplDetailCalcHoursBalance,
      ...pgGroupsDefaultValue,
      employmentInfo: employmentData?.fEmplInfo,
    };
  }, [
    employeeId,
    empBasicData,
    empPosition,
    empWorkContact,
    employmentData,
    employmentDetails,
    allEnums,
    legalEntities,
    orgTree,
    costCenters,
    allEmployees,
    companyData,
    isMainContract,
    getOfficeById,
  ]);

  useEffect(() => {
    if (formTemplates?.length > 0) {
      setSelectedTemplateId(
        formTemplates?.find((pf) => pf?.settings && pf?.settings?.isDefault)
          ?._id ||
          formTemplates[0]?._id ||
          '',
      );
    }
  }, [formTemplates]);

  const formConfigData: FormioFormBuilderConfig | undefined = useMemo(() => {
    if (
      selectedTemplateId &&
      formTemplateDetails &&
      !isCustomConfigLoading &&
      customDataComponents
    ) {
      const formTemplateDetailsCopy = { ...formTemplateDetails };
      const components = formTemplateDetailsCopy?.components || [];
      formTemplateDetailsCopy.components = components;

      //Important: Disable read only fields specific to rehire
      const configWithReadOnlyFields = disableReadOnlyRehireFields(
        formTemplateDetailsCopy,
      );

      const customBottomNavButtons = (formTemplateDetailsCopy.components ||
        [])[0]?.components?.find(
        (comp: FormioComponent) => comp.key === 'navColumnsPage1',
      );
      //Important to remove default nav buttons and add custom nav buttons for improved translations
      const updatedData = customBottomNavButtons
        ? removeNavButtonSettings(configWithReadOnlyFields)
        : configWithReadOnlyFields;

      //Remove SSN field if manager is not allowed to view Social Security Number
      const finalUpdatedData =
        userHasManagerRoleOnly && !managerCanViewSsn
          ? removeSsnFieldIfManagerRestricted(updatedData)
          : updatedData;

      return applyCustomConfig(finalUpdatedData);
    }
    return undefined;
  }, [
    selectedTemplateId,
    formTemplateDetails,
    isCustomConfigLoading,
    customDataComponents,
    applyCustomConfig,
    managerCanViewSsn,
    userHasManagerRoleOnly,
  ]);

  const existingCustomData: ExistingCustomDataItem = useMemo(() => {
    return Object.entries(customDataSubmission ?? {}).reduce(
      (acc, [key, val]) => {
        const newKey = `EMPLOYEE_${key}`;
        const group = `__CUSTOMDATA__EMPLOYEE__${key}`;
        const enumSubitems = allEnums[group];
        if (!enumSubitems) {
          return { ...acc, [newKey]: val };
        }
        if (Array.isArray(val)) {
          return {
            ...acc,
            [newKey]: enumSubitems
              .filter((item) => (val as string[]).includes(item.code))
              .map((item) => {
                return { code: item.code, name: item.name, group };
              }),
          };
        }
        const subItem = enumSubitems.find((sub) => sub.code === val);
        if (!!subItem) {
          return {
            ...acc,
            [newKey]: { code: subItem.code, name: subItem.name, group },
          };
        }

        return { ...acc };
      },
      {},
    );
  }, [customDataSubmission, allEnums]);

  const rehireDataLoading =
    employeeLoading ||
    employmentLoading ||
    isLoadingEmploymentDetails ||
    isLoadingEnums ||
    officeDataLoading ||
    contractsLoading ||
    isCostCentersLoading ||
    isLegalentitiesLoading ||
    isOrgTreeLoading ||
    isCompanyDataLoading ||
    isAllEmployeesLoading ||
    isEmployeeViewsLoading ||
    formTemplatesLoading ||
    latestVersionLoading ||
    formConfigLoading ||
    formTranslationsLoading ||
    isLoadingWorkflowSettings ||
    isCustomConfigLoading ||
    isCustomDataSubmissionLoading;

  if (
    employeeData?.status !== EmployeeStatusEnum.ENDED ||
    (userHasManagerRoleOnly && !managerCanRehire)
  ) {
    history.push(`/portal/employee/${employeeId}/people`);
  }

  if (rehireDataLoading) {
    return {
      rehireDataLoading,
      setSelectedTemplateId,
    };
  }

  return {
    employeeId,
    formTemplates,
    selectedTemplateId,
    formConfigData,
    latestVersion,
    latestVersionLoading,
    formTranslations,
    existingData,
    offices,
    rehireDataLoading,
    workflowConfig,
    existingCustomData,
    refetchTemplateData,
    setSelectedTemplateId,
  };
};
